import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Headphones, Clock, Flag, FlagOff, ChevronRight, ChevronLeft, CheckCircle2,
  AlertTriangle, Loader2, Play, Volume2,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Computer-Based IELTS Listening Simulator.
 * - Full-screen exam env, no sidebar/header
 * - Audio plays ONCE — no pause/rewind/replay controls visible
 * - Reading time before each group (15s)
 * - ~2 min review at end (CBT) or +10 min transfer (paper)
 * - Flag/review, auto-save (state in memory), auto-submit on time-up
 * - Question types: fill, mcq
 */

const READING_TIME_SECS = 15;
const REVIEW_TIME_CBT = 120;
const TRANSFER_TIME_PAPER = 600;
const TOTAL_SECS = 30 * 60 + REVIEW_TIME_CBT; // 32 min CBT; paper handled separately

export default function IELTSListeningMock() {
  const { user } = useAuth();
  const nav = useNavigate();
  const format = user?.ielts_format || "computer";
  const [stage, setStage] = useState("brief"); // brief | reading | listening | review | done
  const [mock, setMock] = useState(null);
  const [partIdx, setPartIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [activeQ, setActiveQ] = useState(1);
  const [secondsLeft, setSecondsLeft] = useState(format === "paper" ? 30 * 60 + TRANSFER_TIME_PAPER : TOTAL_SECS);
  const [readingLeft, setReadingLeft] = useState(READING_TIME_SECS);
  const [reviewLeft, setReviewLeft] = useState(format === "paper" ? TRANSFER_TIME_PAPER : REVIEW_TIME_CBT);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const audioRef = useRef(null);
  const mainTimer = useRef(null);
  const stageTimer = useRef(null);

  useEffect(() => {
    api.get("/ielts/listening-mock").then((r) => setMock(r.data));
  }, []);

  // Master countdown
  useEffect(() => {
    if (stage === "brief" || stage === "done") return;
    mainTimer.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { autoSubmit(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(mainTimer.current);
  }, [stage]); // eslint-disable-line

  // Stage transitions
  const enterReading = () => {
    setStage("reading");
    setReadingLeft(READING_TIME_SECS);
    stageTimer.current = setInterval(() => {
      setReadingLeft((s) => {
        if (s <= 1) { clearInterval(stageTimer.current); enterListening(); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const enterListening = async () => {
    setStage("listening");
    // Start audio for current part
    const part = mock.parts[partIdx];
    const audioUrl = `${process.env.REACT_APP_BACKEND_URL}/api${part.audio_url.replace("/api", "")}`;
    // Use authenticated fetch then create blob URL (audio_url returns mp3 with FastAPI FileResponse + bearer required)
    try {
      const r = await api.get(part.audio_url.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = new Audio(url);
      a.controls = false;
      audioRef.current = a;
      a.onended = () => {
        // Move to next part — if last, go to review
        if (partIdx + 1 < mock.parts.length) {
          setPartIdx((p) => p + 1);
          setActiveQ((q) => mock.parts[partIdx + 1].questions[0].n);
          enterReading();
        } else {
          enterReview();
        }
      };
      try {
        await a.play();
      } catch (e) {
        setAudioBlocked(true);
      }
    } catch (e) {
      toast.error("Audio failed to load");
    }
  };

  const enterReview = () => {
    setStage("review");
    setReviewLeft(format === "paper" ? TRANSFER_TIME_PAPER : REVIEW_TIME_CBT);
    stageTimer.current = setInterval(() => {
      setReviewLeft((s) => {
        if (s <= 1) { clearInterval(stageTimer.current); autoSubmit(); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const beginTest = () => {
    setPartIdx(0);
    setActiveQ(1);
    enterReading();
  };

  const autoSubmit = async () => {
    if (submitting || result) return;
    try { audioRef.current?.pause(); } catch {}
    clearInterval(mainTimer.current);
    clearInterval(stageTimer.current);
    setSubmitting(true);
    try {
      const r = await api.post("/ielts/listening-mock/submit", {
        answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, String(v)])),
        format,
      });
      setResult(r.data);
      setStage("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!mock) return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const allQs = mock.parts.flatMap((p) => p.questions.map((q) => ({ ...q, partTitle: p.title, partId: p.id })));
  const part = mock.parts[partIdx];

  // ============================================================
  // RESULT
  // ============================================================
  if (stage === "done" && result) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 md:p-10" data-testid="listening-result">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-gradient-to-br from-[#003049] to-[#0C4B66] text-white rounded-3xl p-8">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70 mb-2">IELTS Listening Result</div>
            <div className="font-display text-7xl">Band {result.band}</div>
            <div className="text-sm text-white/75 mt-2">
              {result.correct}/{result.total} correct · {format === "paper" ? "Paper-based" : "Computer-based"} simulation
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {result.section_breakdown.map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.part}</div>
                <div className="font-display text-3xl text-[#003049] mt-1">{s.correct}/{s.total}</div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-semibold text-[#003049] mb-3">Answer review</div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto">
              {result.per_question.map((q) => (
                <div key={q.n} className={`rounded-lg border p-3 text-sm ${
                  q.is_correct ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Q{q.n}</span>
                    {q.is_correct
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      : <span className="text-xs text-rose-700">→ {q.correct_answer}</span>}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">Your answer: {q.your_answer || <span className="italic">blank</span>}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => nav("/app")} className="px-5 py-2.5 rounded-full bg-[#003049] text-white text-sm">Dashboard</button>
            <button onClick={() => window.location.reload()} className="px-5 py-2.5 rounded-full border border-slate-200 text-slate-700 text-sm">Take again</button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // BRIEF
  // ============================================================
  if (stage === "brief") {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 md:p-10 flex items-center justify-center" data-testid="listening-brief">
        <div className="max-w-2xl space-y-6">
          <div className="flex items-center gap-3">
            <Headphones className="w-8 h-8 text-[#F77F00]" />
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-white/60">IELTS · {format === "paper" ? "Paper-based" : "Computer-based"} simulation</div>
              <h1 className="font-display text-4xl">{mock.title}</h1>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-2 text-sm leading-relaxed">
            <div className="font-semibold text-white mb-2">Important — read before you begin</div>
            <ul className="space-y-1.5 text-white/85">
              <li>• You will hear 4 audio recordings ({mock.total_questions} questions total)</li>
              <li>• Each recording plays <span className="font-bold text-[#FB7185]">once only</span> — no pause, rewind or replay</li>
              <li>• You'll get {READING_TIME_SECS} seconds to read each set of questions before audio starts</li>
              <li>• At the end you'll get <span className="font-bold text-[#FB7185]">{format === "paper" ? "10 minutes" : "~2 minutes"}</span> to review/transfer answers</li>
              <li>• The test will auto-submit when time runs out</li>
              <li>• Enable your audio and use headphones for best results</li>
            </ul>
          </div>
          <button onClick={beginTest} data-testid="begin-listening-btn"
            className="w-full px-7 py-4 rounded-2xl bg-[#F77F00] hover:bg-[#D26900] text-white font-bold text-lg inline-flex items-center justify-center gap-2">
            <Play className="w-5 h-5" /> Begin Listening Test
          </button>
          <button onClick={() => nav("/app/ielts/start")} className="w-full text-sm text-white/50 hover:text-white">Cancel</button>
        </div>
      </div>
    );
  }

  // ============================================================
  // RUNNING (reading | listening | review)
  // ============================================================
  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col" data-testid={`listening-stage-${stage}`}>
      {/* Top bar */}
      <div className="bg-slate-950 border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Headphones className="w-5 h-5 text-[#F77F00]" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/60">Candidate</div>
            <div className="text-sm font-semibold">{user?.name || "Demo Student"}</div>
          </div>
          <div className="border-l border-white/10 pl-4">
            <div className="text-[10px] uppercase tracking-wider text-white/60">Module</div>
            <div className="text-sm font-semibold">IELTS Listening</div>
          </div>
          <div className="border-l border-white/10 pl-4">
            <div className="text-[10px] uppercase tracking-wider text-white/60">Format</div>
            <div className="text-sm font-semibold">{format === "paper" ? "Paper-based" : "Computer-based"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-[#FB7185]" />
          <div className="font-mono text-2xl tabular-nums" data-testid="listening-timer">
            {fmt(secondsLeft)}
          </div>
        </div>
      </div>

      {/* Stage banner */}
      {stage === "reading" && (
        <div className="bg-amber-500 text-slate-900 px-6 py-3 flex items-center gap-3 font-semibold text-sm" data-testid="reading-banner">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Reading time — you have <span className="font-mono">{readingLeft}s</span> to read Questions {part.questions[0].n}–{part.questions[part.questions.length - 1].n} before the audio begins. <strong>Audio cannot be paused or replayed.</strong></span>
        </div>
      )}
      {stage === "listening" && (
        <div className="bg-[#003049] border-b border-white/10 px-6 py-3 flex items-center gap-3 text-sm" data-testid="listening-banner">
          <Volume2 className="w-4 h-4 text-[#FB7185] animate-pulse" />
          <span>Now listen carefully and answer Questions {part.questions[0].n}–{part.questions[part.questions.length - 1].n}. <span className="text-white/60">The audio will play only once.</span></span>
          {audioBlocked && (
            <button onClick={() => { audioRef.current?.play(); setAudioBlocked(false); }} className="ml-auto px-3 py-1.5 rounded-full bg-[#F77F00] text-xs">
              Tap to enable audio
            </button>
          )}
        </div>
      )}
      {stage === "review" && (
        <div className="bg-emerald-500 text-slate-900 px-6 py-3 flex items-center gap-3 font-semibold text-sm" data-testid="review-banner">
          <CheckCircle2 className="w-4 h-4" />
          <span>{format === "paper" ? "Transfer time" : "Review time"} — you have <span className="font-mono">{fmt(reviewLeft)}</span> to check or change your answers before the test auto-submits.</span>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: question panel */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="text-xs uppercase tracking-wider text-white/50">{part.title}</div>
            <div className="text-sm text-white/70 italic mb-4">{part.context}</div>
            <div className="space-y-3">
              {part.questions.map((q) => (
                <div key={q.n}
                  data-testid={`q-${q.n}`}
                  className={`rounded-xl border p-4 ${activeQ === q.n ? "border-[#F77F00] bg-white/5" : "border-white/10 bg-white/[0.02]"}`}
                  onClick={() => setActiveQ(q.n)}>
                  <div className="flex items-start gap-3">
                    <button onClick={(e) => { e.stopPropagation(); setFlagged((f) => ({ ...f, [q.n]: !f[q.n] })); }}
                      data-testid={`flag-${q.n}`}
                      className="mt-1 text-white/40 hover:text-amber-400">
                      {flagged[q.n] ? <Flag className="w-4 h-4 text-amber-400 fill-amber-400" /> : <FlagOff className="w-4 h-4" />}
                    </button>
                    <div className="text-sm font-semibold w-10 text-white/80">{q.n}.</div>
                    <div className="flex-1">
                      <div className="text-sm text-white/90 mb-2">{q.prompt} <span className="text-[10px] text-white/40">({q.max_words} word{q.max_words > 1 ? "s" : ""} max)</span></div>
                      {q.type === "fill" ? (
                        <input
                          data-testid={`ans-${q.n}`}
                          value={answers[q.n] || ""}
                          onChange={(e) => setAnswers((a) => ({ ...a, [q.n]: e.target.value.toUpperCase() }))}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:border-[#F77F00] focus:outline-none uppercase tracking-wide"
                          placeholder="Type your answer…"
                        />
                      ) : (
                        <div className="space-y-1.5">
                          {q.choices.map((c, ci) => (
                            <label key={ci} className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer ${
                              String(answers[q.n]) === String(ci) ? "bg-[#F77F00]/20 border border-[#F77F00]" : "border border-white/10 hover:border-white/30"
                            }`}>
                              <input type="radio" data-testid={`ans-${q.n}-${ci}`}
                                checked={String(answers[q.n]) === String(ci)}
                                onChange={() => setAnswers((a) => ({ ...a, [q.n]: ci }))} className="mt-1" />
                              <span className="text-sm text-white/90">{c}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: question navigator */}
        <div className="w-64 border-l border-white/10 bg-slate-950 overflow-y-auto p-5 hidden md:block">
          <div className="text-xs uppercase tracking-wider text-white/60 mb-3">Navigator</div>
          {mock.parts.map((p, pi) => (
            <div key={p.id} className="mb-4">
              <div className="text-[10px] text-white/40 mb-2">{p.title}</div>
              <div className="grid grid-cols-5 gap-1.5">
                {p.questions.map((q) => {
                  const filled = answers[q.n] !== undefined && answers[q.n] !== "";
                  const isActive = activeQ === q.n;
                  return (
                    <button key={q.n} onClick={() => setActiveQ(q.n)}
                      disabled={pi !== partIdx && stage === "listening"}
                      data-testid={`nav-${q.n}`}
                      className={`relative w-9 h-9 rounded text-xs font-semibold ${
                        isActive ? "bg-[#F77F00] text-white ring-2 ring-[#F77F00]/40" :
                        filled ? "bg-emerald-600/30 text-emerald-200 border border-emerald-500/50" :
                        "bg-white/5 text-white/60 border border-white/10 hover:bg-white/10"
                      }`}>
                      {q.n}
                      {flagged[q.n] && <Flag className="w-2.5 h-2.5 absolute -top-1 -right-1 text-amber-400 fill-amber-400" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {stage === "review" && (
            <button onClick={autoSubmit} disabled={submitting} data-testid="submit-now-btn"
              className="mt-4 w-full px-4 py-2.5 rounded-lg bg-[#F77F00] hover:bg-[#D26900] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Submit now
            </button>
          )}
        </div>
      </div>

      {/* Time warning */}
      {secondsLeft < 300 && secondsLeft > 0 && stage !== "done" && (
        <div className="bg-rose-600 text-white px-6 py-2 text-center text-sm flex items-center justify-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Less than 5 minutes remaining — your answers will auto-submit.
        </div>
      )}
    </div>
  );
}
